import { useState, useEffect, type FormEvent } from 'react';
import { listUsers, createUser, deleteUser, setUserPassword, setUserCasesAccess, type User, type UserRole } from '../api/client';

interface UsersPageProps {
  currentUserId: string;
}

export function UsersPage({ currentUserId }: UsersPageProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('admin');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [resetId, setResetId] = useState<string | null>(null);
  const [resetPassword, setResetPw] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listUsers());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      await createUser({ username: newUsername, password: newPassword, role: newRole });
      setNewUsername('');
      setNewPassword('');
      setNewRole('admin');
      await load();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string, username: string) {
    if (!confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      await deleteUser(id);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function handleResetSubmit(e: FormEvent) {
    e.preventDefault();
    if (!resetId) return;
    setResetBusy(true);
    setResetError(null);
    try {
      await setUserPassword(resetId, resetPassword);
      setResetId(null);
      setResetPw('');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Users</h2>
      </div>

      <div className="card mb-6">
        <div className="card-header">Create new user</div>
        <div className="card-body">
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>Username</label>
              <input
                className="form-input"
                required
                minLength={2}
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                disabled={creating}
              />
            </div>
            <div className="form-group">
              <label>Password (min 8 chars)</label>
              <input
                className="form-input"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={creating}
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                className="form-input"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                disabled={creating}
              >
                <option value="operator">operator (view only)</option>
                <option value="admin">admin</option>
                <option value="superadmin">superadmin</option>
              </select>
            </div>
            {createError && <div className="login-error">{createError}</div>}
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? 'Creating...' : 'Create user'}
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">All users ({users.length})</div>
        <div className="card-body">
          {loading && <div className="empty-state"><p className="text-muted">Loading...</p></div>}
          {error && <div className="login-error">{error}</div>}
          {!loading && users.length > 0 && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th title="Can open the Complaints section: customer emails and refunds">Complaints</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        {u.username}
                        {u.id === currentUserId && (
                          <span className="text-muted" style={{ fontSize: '0.78rem', marginLeft: 6 }}>(you)</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${u.role === 'superadmin' ? 'badge--warning' : u.role === 'operator' ? 'badge--offline' : 'badge--online'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        {u.role === 'superadmin' ? (
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>always</span>
                        ) : (
                          <input
                            type="checkbox"
                            checked={Boolean(u.canHandleCases)}
                            onChange={async (e) => {
                              try { await setUserCasesAccess(u.id, e.target.checked); await load(); }
                              catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
                            }}
                          />
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(u.createdAt).toLocaleString()}</td>
                      <td>
                        <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setResetId(u.id); setResetPw(''); setResetError(null); }}
                          >
                            Reset password
                          </button>
                          {u.id !== currentUserId && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDelete(u.id, u.username)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {resetId && (
        <div
          onClick={() => !resetBusy && setResetId(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 420, width: '100%' }}
          >
            <div className="card-header">
              Reset password for {users.find((u) => u.id === resetId)?.username}
            </div>
            <div className="card-body">
              <form onSubmit={handleResetSubmit}>
                <div className="form-group">
                  <label>New password (min 8 chars)</label>
                  <input
                    className="form-input"
                    type="password"
                    required
                    minLength={8}
                    autoFocus
                    value={resetPassword}
                    onChange={(e) => setResetPw(e.target.value)}
                    disabled={resetBusy}
                  />
                </div>
                {resetError && <div className="login-error">{resetError}</div>}
                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={resetBusy}>
                    {resetBusy ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={resetBusy} onClick={() => setResetId(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
